import { Link } from '@tanstack/react-router';
import { MdOpenInNew } from 'react-icons/md';
import { format, subMonths } from 'date-fns';
import clsx from 'clsx';
import { Button } from '@/components/Button';
import useFetchDashboardProgress from '../api/useFetchDashboardProgress';
import DashboardFunnelBarChart from './DashboardFunnelBarChart';
import DashboardFunnelChartPlaceholder from './DashboardFunnelChartPlaceholder';

/**
 * 進捗状況セクション。
 *
 * 表示元: GET /agent/api/dashboard/progress（JobChangeProcess.active を current_progress で
 * group_count したスナップショット）
 *
 * - ヘッダ: 「進捗状況」 + 「分析機能」リンクボタン
 * - サマリーカード: 書類選考 / 面談 / 1~4次選考 / 最終選考 / 内定 / 決定 の 6 項目
 * - グラフ: DashboardFunnelBarChart（current_progress の 10 段表示、表示順固定）
 *
 * 担当者セレクトと連動する（props で organizationUserId を受け取る）。
 */

type DashboardProgressSectionProps = {
  organizationUserId?: string;
};

/** グラフ表示用 10 段の順序（current_progress ベース） */
const CHART_STEPS: { apiKey: string; label: string }[] = [
  { apiKey: 'document_screening', label: '書類選考' },
  { apiKey: 'meeting', label: '面談' },
  { apiKey: '1st_interview', label: '1次選考' },
  { apiKey: '2nd_interview', label: '2次選考' },
  { apiKey: '3rd_interview', label: '3次選考' },
  { apiKey: '4th_interview', label: '4次選考' },
  { apiKey: 'final_interview', label: '最終面接' },
  { apiKey: 'offer_meeting', label: 'オファー面談' },
  { apiKey: 'offered', label: '内定' },
  { apiKey: 'closed', label: '決定' },
];

/** サマリーカード 6 枚の定義 */
const SUMMARY_ITEMS: {
  key: string;
  label: string;
  unit: '名' | '件';
  apiKeys: string[];
}[] = [
  { key: 'document_screening', label: '書類選考', unit: '件', apiKeys: ['document_screening'] },
  { key: 'meeting', label: '面談', unit: '件', apiKeys: ['meeting'] },
  {
    key: 'interviews',
    label: '1~4次選考',
    unit: '件',
    apiKeys: ['1st_interview', '2nd_interview', '3rd_interview', '4th_interview'],
  },
  {
    key: 'final',
    label: '最終選考',
    unit: '件',
    apiKeys: ['final_interview', 'offer_meeting'],
  },
  { key: 'offered', label: '内定', unit: '件', apiKeys: ['offered'] },
  { key: 'closed', label: '決定', unit: '件', apiKeys: ['closed'] },
];

/**
 * 「もっと見る」リンク先のクエリパラメータ。
 * 分析ルートの validateSearch（analysisSearchSchema）が必須フィールドを持つため、
 * 遷移時点の日付でデフォルト値を補完して渡す。
 */
function buildLinkSearch() {
  const today = new Date();
  return {
    tab: 'progress' as const,
    from: format(subMonths(today, 1), 'yyyy-MM-dd'),
    to: format(today, 'yyyy-MM-dd'),
    granularity: 'day' as const,
    metric: 'count' as const,
    count_mode: 'total' as const,
    insight_metric: 'lead_time' as const,
    sales_trend_mode: 'forecast' as const,
    filter_target: 'candidate' as const,
    page: 1,
  };
}

export default function DashboardProgressSection({
  organizationUserId,
}: DashboardProgressSectionProps) {
  const { data, isLoading, isError } = useFetchDashboardProgress(organizationUserId);

  const hasData = !isError && !!data && data.summary.length > 0;

  /** apiKeys を summary から合算して値を出す */
  const aggregate = (apiKeys: string[]) => {
    if (!data) return 0;
    return data.summary
      .filter((s) => apiKeys.includes(s.step))
      .reduce((sum, i) => sum + i.count, 0);
  };

  return (
    <section className="flex flex-col gap-xs rounded-sm bg-white px-md pt-xs pb-md shadow-[0_0_3px_0_rgba(0,0,0,0.2)]">
      {/* ヘッダ */}
      <div className="flex items-center justify-between">
        <h2 className="text-sm leading-normal font-bold text-[#101828]">進捗状況</h2>
        <Button asChild color="tertiary" size="md">
          <Link to="/agent/analysis" search={buildLinkSearch()}>
            <MdOpenInNew size={20} aria-hidden="true" />
            もっと見る
          </Link>
        </Button>
      </div>

      {isLoading ? (
        <DashboardFunnelChartPlaceholder variant="loading" />
      ) : !hasData ? (
        <DashboardFunnelChartPlaceholder variant="empty" />
      ) : (
        <>
          {/* サマリーカード（shrink-0 で内容幅、items 間 gap-16 / 値 w-55 固定） */}
          <div className="inline-flex w-fit shrink-0 items-center gap-md overflow-clip rounded-sm bg-white px-lg py-sm shadow-[0_0_2px_0_rgba(0,0,0,0.2)]">
            {SUMMARY_ITEMS.map((item, index) => {
              const isLast = index === SUMMARY_ITEMS.length - 1;
              const value = aggregate(item.apiKeys);
              return (
                <div
                  key={item.key}
                  className={clsx(
                    'flex shrink-0 flex-col items-start justify-center pr-sm',
                    !isLast && 'border-r border-solid border-border'
                  )}
                >
                  <p className="text-xs leading-[1.7] font-normal whitespace-nowrap text-sub">
                    {item.label}
                  </p>
                  <p className="w-[55px] text-xl leading-[1.4] font-bold whitespace-nowrap text-main">
                    {value.toLocaleString()}
                    {item.unit}
                  </p>
                </div>
              );
            })}
          </div>

          {/* グラフ（件数ベース、current_progress の 10 段表示） */}
          <div className="rounded-sm">
            <DashboardFunnelBarChart
              items={CHART_STEPS.map((step) => ({
                step: step.apiKey,
                label: step.label,
                value: data?.summary.find((s) => s.step === step.apiKey)?.count ?? 0,
              }))}
            />
          </div>
        </>
      )}
    </section>
  );
}
